import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { ProductImage } from "@/components/storefront/ProductImage";
import { useCart } from "@/hooks/storefront/use-cart";
import { listMrBoxes } from "@/lib/mrbox.functions";
import { formatPrice, productSlug } from "@/lib/format";
import type { MrBox } from "@/lib/mrbox";
import { ShoppingCart, Sparkles, PackageOpen, Gift, Lock } from "lucide-react";

export const Route = createFileRoute("/mrbox")({
  head: () => ({
    meta: [
      { title: "MrBox — Έτοιμα πακέτα προσφορών | MrPanda.gr" },
      {
        name: "description",
        content:
          "Έτοιμα θεματικά πακέτα MrBox: καθαριότητας, πρωινού, μπάνιου και άλλα — με αυτόματη έκπτωση 4–8%. Πρόσθεσέ τα στο καλάθι με ένα κλικ.",
      },
      { property: "og:title", content: "MrBox — Έτοιμα πακέτα προσφορών" },
      {
        property: "og:description",
        content: "Θεματικά πακέτα προϊόντων με έκπτωση 4–8%. Όλα έτοιμα, σε μία κίνηση.",
      },
      { property: "og:url", content: "https://mrpanda.gr/mrbox" },
    ],
  }),
  component: MrBoxPage,
});

const PAGE = 6;

function MrBoxPage() {
  // Σταδιακή φόρτωση: δείχνουμε τα πρώτα 6 πακέτα αμέσως και συνεχίζουμε να
  // φέρνουμε τα υπόλοιπα στο παρασκήνιο, ώστε η σελίδα να μην «κολλάει».
  const [boxes, setBoxes] = useState<MrBox[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [more, setMore] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let offset = 0;
      let total = Number.POSITIVE_INFINITY;
      while (!cancelled && offset < total) {
        const res = await listMrBoxes({ data: { offset, limit: PAGE } });
        if (cancelled) return;
        total = res.total || 0;
        setBoxes((prev) => [...prev, ...res.boxes]);
        setIsLoading(false);
        offset += PAGE;
      }
      if (!cancelled) setMore(false);
    })().catch(() => {
      if (!cancelled) {
        setIsLoading(false);
        setMore(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-none px-4 pb-16 pt-6">
        {/* Hero */}
        <div className="mp-playful-panel relative mb-8 overflow-hidden rounded-3xl border border-border p-8 text-center shadow-sm">
          <div className="pointer-events-none absolute -left-3 top-3 select-none text-5xl opacity-20 animate-bounce">📦</div>
          <div className="pointer-events-none absolute right-4 top-5 select-none text-3xl opacity-20">🐼</div>
          <div className="mb-3 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary">
            <PackageOpen className="h-8 w-8" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">MrBox — Έτοιμα πακέτα</h1>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground">
            Διάλεξε ένα έτοιμο πακέτο και πάρε τα πάντα μαζί! 🎁
          </p>
        </div>

        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-96 animate-pulse rounded-3xl border border-border bg-muted/40" />
            ))}
          </div>
        ) : boxes.length === 0 ? (
          <div className="mx-auto max-w-md rounded-3xl border border-border p-12 text-center mp-playful-panel">
            <div className="mb-3 text-6xl">📦🐼</div>
            <h2 className="text-lg font-bold">Τα πακέτα ετοιμάζονται…</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Δοκίμασε ξανά σε λίγο ή δες όλα τα προϊόντα μας.
            </p>
            <Link
              to="/"
              className="mp-bouncy mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm"
            >
              🛍️ Πάμε για ψώνια
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {boxes.map((box) => (
              <MrBoxCard key={box.id} box={box} />
            ))}
            {more &&
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={`sk-${i}`}
                  className="h-96 animate-pulse rounded-3xl border border-border bg-muted/40"
                />
              ))}
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}

function MrBoxCard({ box }: { box: MrBox }) {
  const cart = useCart();
  const pctLabel = Math.round(box.discountPct * 100);

  const mysteryLabel = `Mystery Box — ${box.category ?? "Έκπληξη"}`;

  const addBundle = () => {
    // Μοναδικό κλειδί αντιτύπου: επιτρέπει πολλά ίδια πακέτα στο καλάθι,
    // το καθένα με τη δική του έκπτωση.
    const bundleKey = `${box.id}#${Math.random().toString(36).slice(2, 10)}`;
    box.items.forEach((it, idx) => {
      cart.add(
        {
          productId: it.productId,
          psId: it.psId,
          name: it.name,
          price: it.price,
          imageUrl: it.imageUrl,
          originalPrice: it.originalPrice,
          stock: it.stock,
          bundleId: box.id,
          bundleKey,
          bundlePct: box.discountPct,
          bundleTitle: box.mystery ? mysteryLabel : box.title,
          // Mystery box: ο πελάτης βλέπει ΜΟΝΟ μία γραμμή με την κατηγορία και
          // την αξία του πακέτου — τα προϊόντα μένουν κρυφά μέχρι την παραλαβή.
          hidden: box.mystery ? idx > 0 : false,
          displayName: box.mystery && idx === 0 ? mysteryLabel : null,
          mysteryTotal: box.mystery && idx === 0 ? box.regularTotal : null,
        },
        it.quantity,
      );
    });
    toast.success(`Το «${box.title}» μπήκε στο καλάθι! 🎉`, {
      description: `Κέρδισες ${formatPrice(box.savings)} (−${pctLabel}%) σαν πακέτο.`,
    });
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
      {/* Header */}
      <div className={`relative bg-gradient-to-br ${box.accent} p-5 text-white`}>
        <div className="text-4xl">{box.emoji}</div>
        <h2 className="mt-2 text-lg font-extrabold leading-tight">{box.title}</h2>
        <p className="mt-0.5 text-xs text-white/90">{box.tagline}</p>
      </div>

      {/* Items — στα mystery boxes το περιεχόμενο μένει κρυφό */}
      {box.mystery ? (
        <div className="flex-1 px-4 py-6 text-center">
          <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-primary/10 text-4xl">
            🎁
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-bold">
            <Gift className="h-3.5 w-3.5" /> {box.category ?? "Έκπληξη"}
          </div>
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" /> Το περιεχόμενο αποκαλύπτεται μόλις παραλάβεις το
            πακέτο.
          </p>
        </div>
      ) : (
      <ul className="flex-1 divide-y divide-border px-4">
        {box.items.map((it) => (
          <li key={it.productId}>
            <Link
              to="/p/$slug"
              params={{ slug: productSlug(it.name, it.psId, it.productId) }}
              className="flex items-center gap-3 py-2.5 transition-colors hover:bg-muted/50"
            >
              <span className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-white p-1.5">
                <ProductImage
                  src={it.imageUrl}
                  alt={it.name}
                  width={96}
                  className="h-full w-full object-contain"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  fallback={<span className="grid h-full w-full place-items-center text-xl">🐼</span>}
                />
              </span>

              <span className="min-w-0 flex-1 break-words text-xs leading-snug hover:underline">
                {it.name}
              </span>
              {it.quantity > 1 && (
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                  ×{it.quantity}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
      )}

      {/* Price + CTA */}
      <div className="border-t border-border p-4">
        <div className="mb-3 flex items-end justify-between">
          <div>
            <div className="text-xs text-muted-foreground line-through">
              {formatPrice(box.regularTotal)}
            </div>
            <div className="text-2xl font-extrabold text-price">{formatPrice(box.discountedTotal)}</div>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2.5 py-1 text-xs font-bold text-success">
            <Sparkles className="h-3.5 w-3.5" /> −{formatPrice(box.savings)}
          </span>
        </div>
        <button
          type="button"
          onClick={addBundle}
          className="mp-bouncy flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm hover:opacity-90"
        >
          <ShoppingCart className="h-4 w-4" /> Προσθήκη στο καλάθι
        </button>
      </div>
    </div>
  );
}